import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, signUpMaster, resendVerification, logOut } from '../firebase/auth';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useAuth } from '../firebase/AuthContext';
 
// Password rules — must match Firebase Console settings exactly
const validatePassword = (pwd) => {
  const checks = {
    length:    pwd.length >= 10,
    uppercase: /[A-Z]/.test(pwd),
    lowercase: /[a-z]/.test(pwd),
    number:    /[0-9]/.test(pwd),
    special:   /[^A-Za-z0-9]/.test(pwd),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  return { checks, passed, total: 5 };
};
 
const strengthLabel = (passed) => {
  if (passed <= 2) return { label: 'Weak',   color: '#dc2626', bg: '#fef2f2' };
  if (passed <= 3) return { label: 'Fair',   color: '#d97706', bg: '#fffbeb' };
  if (passed === 4) return { label: 'Good',  color: '#2563EB', bg: '#eff6ff' };
  return               { label: 'Strong', color: '#16a34a', bg: '#f0fdf4' };
};
 
function Login() {
  const navigate = useNavigate();
  const { user, emailVerified, checkVerification, loading: authLoading } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
 
  const pwdValidation = validatePassword(password);
  const strength = strengthLabel(pwdValidation.passed);

  useEffect(() => {
    if (!authLoading && user && emailVerified) {
      navigate('/');
    }
  }, [user, emailVerified, authLoading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    }
    setLoading(false);
  };
 
  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!companyName.trim()) return setError('Company name is required.');
    if (!pwdValidation.checks.length)    return setError('Password must be at least 10 characters.');
    if (!pwdValidation.checks.uppercase) return setError('Password must contain at least one uppercase letter.');
    if (!pwdValidation.checks.lowercase) return setError('Password must contain at least one lowercase letter.');
    if (!pwdValidation.checks.number)    return setError('Password must contain at least one number.');
    if (!pwdValidation.checks.special)   return setError('Password must contain at least one special character (!@#$%^&* etc).');
    if (password !== confirm)            return setError('Passwords do not match.');
    setLoading(true);
    try {
      await signUpMaster(email, password, companyName);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };
 
  const handleForgotPassword = async () => {
    setError('');
    setResetSent(false);
    if (!email) {
      setError('Enter your email address above, then click Forgot password.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      setError('Could not send reset email. Make sure the address is correct.');
    }
  };
 
  const handleResend = async () => {
    setResendSent(false);
    try {
      await resendVerification();
      setResendSent(true);
    } catch (err) {
      setError('Could not resend. Please wait a moment and try again.');
    }
  };
 
  const handleCheckVerification = async () => {
    setCheckingVerification(true);
    setError('');
    const verified = await checkVerification();
    if (verified) {
      navigate('/');
    } else {
      setError('Email not verified yet. Check your inbox and click the link first.');
    }
    setCheckingVerification(false);
  };
 
  const handleSignOutAndReset = async () => {
    await logOut();
    setMode('login');
    setEmail('');
    setPassword('');
    setCompanyName('');
    setConfirm('');
    setError('');
    setResetSent(false);
    setResendSent(false);
  };
 
  // Verify email screen
  if (user && !emailVerified) {
    return (
      <div style={styles.page}>
        <div style={styles.left}>
          <div style={styles.leftInner}>
            <div style={styles.brand}>
              <div style={styles.brandLogo}>M<span style={styles.brandBlue}>I</span>A</div>
              <div style={styles.brandSub}>Manufacturing Inspection Assistant</div>
            </div>
            <div style={styles.tagline}>
              One more step.<br />
              <span style={styles.taglineBlue}>Verify your email to continue.</span>
            </div>
            <div style={styles.subTagline}>
              We sent a verification link to your inbox. Click it to activate your MIA account and start your 30-day free trial.
            </div>
            <div style={styles.bottomBar}>
              <div style={styles.domainLabel}>faiready.com</div>
              <div style={styles.betaBadge}>Beta</div>
            </div>
          </div>
        </div>
        <div style={styles.right}>
          <div style={styles.card}>
            <div style={{ padding: '32px' }}>
              <div style={styles.verifyIcon}>✉️</div>
              <div style={styles.formTitle}>Check your inbox</div>
              <div style={styles.formSub}>
                We sent a verification link to <strong>{user.email}</strong>.
                Click the link in that email, then come back here and click the button below.
              </div>
              {resendSent && (
                <div style={styles.successBox}>Verification email resent — check your inbox.</div>
              )}
              {error && <div style={styles.error}>{error}</div>}
              <button
                style={{ ...styles.submitBtn, marginBottom: '12px' }}
                onClick={handleCheckVerification}
                disabled={checkingVerification}
              >
                {checkingVerification ? 'Checking...' : "I've verified my email →"}
              </button>
              <button style={styles.secondaryBtn} onClick={handleResend}>
                Resend verification email
              </button>
              <div style={{ ...styles.switchText, marginTop: '16px' }}>
                Wrong email address?{' '}
                <span style={styles.switchLink} onClick={handleSignOutAndReset}>Start over</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
 
  return (
    <div style={styles.page}>
      <div style={styles.left}>
        <div style={styles.leftInner}>
          <div style={styles.brand}>
            <div style={styles.brandLogo}>M<span style={styles.brandBlue}>I</span>A</div>
            <div style={styles.brandSub}>Manufacturing Inspection Assistant</div>
          </div>
          <div style={styles.tagline}>
            Upload the print.<br />
            <span style={styles.taglineBlue}>Build the inspection package.</span>
          </div>
          <div style={styles.subTagline}>
            MIA gives you the structure, workflow, and output format to complete inspection documentation fast — without starting from scratch every time.
          </div>
          <div style={styles.features}>
            <div style={styles.feature}>
              <div style={styles.featureIcon}>◎</div>
              <div>
                <div style={styles.featureTitle}>Bubble placement</div>
                <div style={styles.featureDesc}>Click to place numbered inspection callouts on any drawing</div>
              </div>
            </div>
            <div style={styles.feature}>
              <div style={styles.featureIcon}>☰</div>
              <div>
                <div style={styles.featureTitle}>FAI & final inspection</div>
                <div style={styles.featureDesc}>Professional reports generated automatically</div>
              </div>
            </div>
            <div style={styles.feature}>
              <div style={styles.featureIcon}>↓</div>
              <div>
                <div style={styles.featureTitle}>Export packages</div>
                <div style={styles.featureDesc}>Bubbled PDF, CSV, JSON — customer-ready in one click</div>
              </div>
            </div>
          </div>
          <div style={styles.bottomBar}>
            <div style={styles.domainLabel}>faiready.com</div>
            <div style={styles.betaBadge}>Beta</div>
          </div>
        </div>
      </div>
 
      <div style={styles.right}>
        <div style={styles.card}>
          <div style={styles.tabs}>
            <button
              style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }}
              onClick={() => { setMode('login'); setError(''); setResetSent(false); }}
            >
              Sign In
            </button>
            <button
              style={{ ...styles.tab, ...(mode === 'signup' ? styles.tabActive : {}) }}
              onClick={() => { setMode('signup'); setError(''); setResetSent(false); }}
            >
              Create Account
            </button>
          </div>
 
          {mode === 'login' ? (
            <form onSubmit={handleLogin} style={styles.form}>
              <div style={styles.formTitle}>Welcome back</div>
              <div style={styles.formSub}>Sign in to your MIA account</div>
 
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Email address</label>
                <input
                  type="email"
                  style={styles.input}
                  placeholder="you@yourcompany.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
 
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Password</label>
                <input
                  type="password"
                  style={styles.input}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
                <span style={styles.forgotLink} onClick={handleForgotPassword}>
                  Forgot password?
                </span>
              </div>
 
              {resetSent && (
                <div style={styles.successBox}>
                  Password reset email sent — check your inbox.
                </div>
              )}
              {error && <div style={styles.error}>{error}</div>}
 
              <button
                type="submit"
                style={{ ...styles.submitBtn, ...(loading ? styles.submitDisabled : {}) }}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In →'}
              </button>
 
              <div style={styles.switchText}>
                Don't have an account?{' '}
                <span style={styles.switchLink} onClick={() => { setMode('signup'); setError(''); }}>
                  Create one
                </span>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSignup} style={styles.form}>
              <div style={styles.formTitle}>Create your account</div>
              <div style={styles.formSub}>30-day free trial — no credit card required</div>
 
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Company name</label>
                <input
                  style={styles.input}
                  placeholder="e.g. Carlson Hydraulics"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  required
                />
              </div>
 
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Email address</label>
                <input
                  type="email"
                  style={styles.input}
                  placeholder="you@yourcompany.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
 
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Password</label>
                <input
                  type="password"
                  style={{ ...styles.input, marginBottom: '8px' }}
                  placeholder="Min 10 chars, upper, lower, number, special"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
 
                {/* Strength bar — only shows once user starts typing */}
                {password.length > 0 && (
                  <div>
                    {/* Progress bar */}
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                      {[1,2,3,4,5].map(i => (
                        <div key={i} style={{
                          flex: 1,
                          height: '4px',
                          borderRadius: '2px',
                          background: i <= pwdValidation.passed ? strength.color : '#e5e7eb',
                          transition: 'background 0.2s',
                        }} />
                      ))}
                    </div>
 
                    {/* Strength label + checklist */}
                    <div style={{
                      background: strength.bg,
                      border: `1px solid ${strength.color}33`,
                      borderRadius: '7px',
                      padding: '10px 12px',
                      marginBottom: '4px',
                    }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: strength.color, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {strength.label}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px' }}>
                        {[
                          { key: 'length',    label: '10+ characters' },
                          { key: 'uppercase', label: 'Uppercase letter' },
                          { key: 'lowercase', label: 'Lowercase letter' },
                          { key: 'number',    label: 'Number' },
                          { key: 'special',   label: 'Special character' },
                        ].map(({ key, label }) => (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: pwdValidation.checks[key] ? '#16a34a' : '#9ca3af' }}>
                            <span>{pwdValidation.checks[key] ? '✓' : '○'}</span>
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
 
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Confirm password</label>
                <input
                  type="password"
                  style={{
                    ...styles.input,
                    borderColor: confirm.length > 0
                      ? password === confirm ? '#16a34a' : '#dc2626'
                      : '#e5e7eb'
                  }}
                  placeholder="••••••••"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                />
                {confirm.length > 0 && password !== confirm && (
                  <span style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px' }}>
                    Passwords do not match
                  </span>
                )}
                {confirm.length > 0 && password === confirm && (
                  <span style={{ fontSize: '11px', color: '#16a34a', marginTop: '2px' }}>
                    ✓ Passwords match
                  </span>
                )}
              </div>
 
              {error && <div style={styles.error}>{error}</div>}
 
              <button
                type="submit"
                style={{ ...styles.submitBtn, ...(loading ? styles.submitDisabled : {}) }}
                disabled={loading}
              >
                {loading ? 'Creating account...' : 'Create Account →'}
              </button>
 
              <div style={styles.switchText}>
                Already have an account?{' '}
                <span style={styles.switchLink} onClick={() => { setMode('login'); setError(''); }}>
                  Sign in
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
 
const styles = {
  page: { display: 'flex', height: '100vh', fontFamily: 'Inter, sans-serif', overflow: 'hidden' },
  left: { width: '45%', background: '#0d1b2a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', flexShrink: 0 },
  leftInner: { maxWidth: '380px', width: '100%', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' },
  brand: { marginBottom: '0' },
  brandLogo: { fontSize: '52px', fontWeight: '800', color: '#fff', letterSpacing: '-2px', lineHeight: 1 },
  brandBlue: { color: '#378ADD' },
  brandSub: { fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '8px', letterSpacing: '2px', textTransform: 'uppercase' },
  tagline: { fontSize: '34px', fontWeight: '600', color: '#fff', lineHeight: '1.2', letterSpacing: '-0.5px' },
  taglineBlue: { color: '#378ADD' },
  subTagline: { fontSize: '15px', color: 'rgba(255,255,255,0.45)', lineHeight: '1.65', marginTop: '16px' },
  features: { display: 'flex', flexDirection: 'column', gap: '20px' },
  feature: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  featureIcon: { width: '38px', height: '38px', background: 'rgba(55,138,221,0.12)', border: '1px solid rgba(55,138,221,0.22)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', color: '#378ADD', flexShrink: 0 },
  featureTitle: { fontSize: '14px', fontWeight: '600', color: '#e2e8f0', marginBottom: '3px' },
  featureDesc: { fontSize: '12.5px', color: 'rgba(255,255,255,0.38)', lineHeight: '1.5' },
  bottomBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '20px' },
  domainLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.3px' },
  betaBadge: { fontSize: '11px', background: 'rgba(55,138,221,0.15)', color: '#378ADD', border: '1px solid rgba(55,138,221,0.2)', padding: '3px 10px', borderRadius: '20px' },
  right: { flex: 1, background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' },
  card: { background: '#fff', borderRadius: '16px', border: '1px solid #e5e7eb', width: '100%', maxWidth: '420px', overflow: 'hidden' },
  tabs: { display: 'flex', borderBottom: '1px solid #e5e7eb' },
  tab: { flex: 1, padding: '14px', fontSize: '13.5px', fontWeight: '500', color: '#9ca3af', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit' },
  tabActive: { color: '#111827', borderBottom: '2px solid #2563EB', fontWeight: '600' },
  form: { padding: '28px', overflowY: 'auto', maxHeight: 'calc(100vh - 60px)' },
  formTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '4px' },
  formSub: { fontSize: '12.5px', color: '#9ca3af', marginBottom: '24px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  label: { fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13.5px', color: '#111827', fontFamily: 'inherit', outline: 'none' },
  forgotLink: { fontSize: '12px', color: '#2563EB', cursor: 'pointer', alignSelf: 'flex-end', marginTop: '2px' },
  successBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 14px', color: '#15803d', fontSize: '12.5px', marginBottom: '16px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', color: '#dc2626', fontSize: '12.5px', marginBottom: '16px' },
  submitBtn: { width: '100%', background: '#2563EB', border: 'none', borderRadius: '9px', padding: '12px', fontSize: '14px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '16px' },
  submitDisabled: { background: '#93c5fd', cursor: 'not-allowed' },
  secondaryBtn: { width: '100%', background: 'transparent', border: '1px solid #e5e7eb', borderRadius: '9px', padding: '11px', fontSize: '13.5px', fontWeight: '500', color: '#6b7280', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '8px' },
  switchText: { fontSize: '12.5px', color: '#9ca3af', textAlign: 'center' },
  switchLink: { color: '#2563EB', fontWeight: '600', cursor: 'pointer' },
  verifyIcon: { fontSize: '40px', textAlign: 'center', marginBottom: '12px' },
};
 
export default Login;